<template>
  <BuilderWrapper
    v-if="showOnDataVisualisationOrConsolidatedCustomVisualisation"
    title="Splash Screen"
    helpful-tip="The splash screen is an optional feature that can be used to display information to the user when they load the application."
    :show-premium-feature="!featureEnabled"
    :expand-disabled="!featureEnabled"
    data-test="splash-screen-settings"
  >
    <LabelledInput>
      <b-checkbox
        v-if="featureEnabled"
        v-model="showSplashScreen"
      >
        Enable splash screen
      </b-checkbox>
    </LabelledInput>

    <template v-if="showSettings">
      <LabelledInput
        label="Title"
      >
        <b-input v-model="title" />
      </LabelledInput>

      <LabelledInput
        label="Display"
        helpful-tip="Choose whether to display the splashscreen on desktop, mobile or both"
      >
        <b-select
          v-model="display"
          :options="displayOptions"
        />
      </LabelledInput>

      <LabelledInput
        label="Maximum width"
        helpful-tip="The width will be reduced on smaller screens to avoid horizontal scroll bars"
      >
        <b-select
          v-model="width"
          :options="widthOptions"
        />
      </LabelledInput>

      <LabelledInput>
        <b-button
          variant="info"
          block
          @click="showModal"
        >
          Show splash screen
        </b-button>
      </LabelledInput>

      <TTNoField
        :config-path="[SPLASH_SCREEN_CONFIG_KEY,'content']"
        :panel="SPLASH_SCREEN"
      />
    </template>
  </BuilderWrapper>
</template>

<script lang="ts">

import { PropType } from 'vue'
import Component from 'vue-class-component'

import BuilderWrapper from '@/components/builder/UI/BuilderWrapper.vue'
import LabelledInput from '@/components/builder/UI/LabelledInput.vue'
import TTNoField from '@/components/TipTap/wrappers/TTNoField.vue'
import FeatureSetMixin from '@/mixins/FeatureSetMixin'
import { FeatureKeys, StoryKeys, SplashScreenDisplays, SplashScreenConfig } from '@/types'
import {
  SPLASH_SCREEN_CONFIG_KEY,
  CONFIG_ENABLED_KEY,
  STORY_SETTINGS_CONFIG_KEY,
} from '@/constants'

const components = {
  BuilderWrapper,
  LabelledInput,
  TTNoField,
}

const props = {
  exclude: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
}

@Component({ components })
export default class SplashScreenSettings extends FeatureSetMixin.extend({ props }) {
  SPLASH_SCREEN_CONFIG_KEY = SPLASH_SCREEN_CONFIG_KEY
  SPLASH_SCREEN = StoryKeys.SPLASH_SCREEN
  widthOptions = [
    { value: 'xl', text: 'Extra-Large' },
    { value: 'lg', text: 'Large' },
    { value: 'md', text: 'Medium' },
    { value: 'sm', text: 'Small' },
  ]

  displayOptions = [
    { value: SplashScreenDisplays.BOTH, text: 'Mobile and Desktop' },
    { value: SplashScreenDisplays.MOBILE, text: 'Mobile only' },
    { value: SplashScreenDisplays.DESKTOP, text: 'Desktop only' },
  ]

  featureKey = FeatureKeys.SPLASH_SCREEN
  featureEnabled!: boolean

  get isCustom () {
    return this.$typedStore.activeVisualisation.isCustom
  }

  get isVisualisationApp () {
    return this.$typedStore.primary.app.isVisualisationApp
  }

  get isConsolidatedApp () {
    return this.$typedStore.primary.app.isConsolidatedApp
  }

  get showOnConsolidatedCustomVisualisationsOnly () {
    return this.isConsolidatedApp && this.isCustom
  }

  get showOnDataVisualisationOrConsolidatedCustomVisualisation () {
    return this.showOnConsolidatedCustomVisualisationsOnly || this.isVisualisationApp
  }

  get showSettings () {
    return this.featureEnabled && this.showSplashScreen && !this.exclude.includes('settings')
  }

  get splashScreen () {
    return this.$typedStore.primary.app.config.splashScreen
  }

  get showSplashScreen () {
    return this.splashScreen?.enabled ?? false
  }

  set showSplashScreen (value) {
    this.saveConfig(value)
    this.addStorySettings()
    // Change display settings to hide splash screen, else pops up immediately
    this.$typedStore.public.display.setShowSplashScreen(false)
  }

  get title () {
    return this.splashScreen?.title || ''
  }

  set title (value) {
    this.updateConfig(value, SPLASH_SCREEN_CONFIG_KEY, 'title')
  }

  get width () {
    return this.splashScreen?.width || 'md'
  }

  set width (value) {
    this.updateConfig(value, SPLASH_SCREEN_CONFIG_KEY, 'width')
  }

  get display () {
    return this.splashScreen?.display || SplashScreenDisplays.BOTH
  }

  set display (value) {
    this.updateConfig(value, SPLASH_SCREEN_CONFIG_KEY, 'display')
  }

  updateConfig (value: unknown, ...args: [typeof SPLASH_SCREEN_CONFIG_KEY] | [typeof SPLASH_SCREEN_CONFIG_KEY, keyof SplashScreenConfig] | [typeof STORY_SETTINGS_CONFIG_KEY, typeof StoryKeys.SPLASH_SCREEN] | []) {
    this.$typedStore.primary.app.updateConfig({
      configPath: args,
      value,
    })
  }

  showModal () {
    this.$typedStore.public.display.setShowSplashScreen(true)
  }

  saveConfig (value: boolean) {
    if (this.$typedStore.primary.app.config.splashScreen) {
      this.updateConfig(value, SPLASH_SCREEN_CONFIG_KEY, CONFIG_ENABLED_KEY)
    } else {
      this.updateConfig({ content: '', enabled: value, title: '', width: 'md' }, SPLASH_SCREEN_CONFIG_KEY)
    }
  }

  addStorySettings () {
    if (!this.$typedStore.primary.app.config.storySettings.splashscreen) {
      this.updateConfig({
        fontSize: 100,
        fontColor: '#000000',
        fontFamily: 'Arial',
        headingColor: '',
        linkColor: '',
      }, STORY_SETTINGS_CONFIG_KEY, StoryKeys.SPLASH_SCREEN)
    }
  }
}

</script>
